import {
  addHours,
  addMonths,
  differenceInCalendarDays,
  differenceInMinutes,
  endOfMonth,
  format,
  isAfter,
  isEqual,
  isSameDay,
  isSameYear,
  isValid,
  parseISO,
  setMilliseconds,
  setSeconds,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import moment, { Moment } from "moment-timezone";
import {
  GlobalFilterOptionsEnum,
  IAppliedFilterValues,
  RollingHoursInput,
  RollingHoursOutput,
  TodayFilterOptionsEnum,
} from "../../enum/global-filter/GlobalFilterEnum";
import { IGlobalDateFilter } from "../../models/filters/IFilters";
import { getAirportConfig } from "./CommonHelperFunctions";

export const dateFnsFormat = "dd-MM-yy HH:mm";
export const payloadDateFormat = "yyyy-MM-dd'T'HH:mm:ss";
const MINUTES_IN_DAY = 1440; // 24 * 60

/** Get the current GMT offset */
export const getGMTOffset = (date: Date) => {
  const dateString = date.toString();
  const gmtMatch = dateString.match(/GMT[+-]\d{4}/);
  return gmtMatch ? gmtMatch[0] : "GMT offset not found";
};

/** Parse the GMT offset from a string and return the total minutes */
export function parseGmtOffset(offset: string): number {
  const sign = offset[3] === "+" ? 1 : -1;
  const hours = parseInt(offset.slice(4, 6), 10);
  const minutes = parseInt(offset.slice(6, 8), 10);
  return sign * (hours * 60 + minutes);
}

/**
 * Get the time difference between two GMT offsets
 * @param {string} offset1 - Time zone Offset1
 * @param {string} offset2 - Time zone Offset2
 * @returns { hours: number; minutes: number; sign: string } Time zone difference in hours and minutes
 */
export function getTimeDifference(
  offset1: string,
  offset2: string
): { hours: number; minutes: number; sign: string } {
  const difference = parseGmtOffset(offset2) - parseGmtOffset(offset1);
  const hours = Math.trunc(difference / 60);
  const minutes = Math.trunc(difference % 60);
  const sign = difference >= 0 ? "+" : "-";
  return { hours, minutes, sign };
}

export function convertToGMTOffset(timeZoneId: string): string {
  try {
    const now = new Date();

    // Format the date in the target timezone and extract the offset
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZoneId,
      timeZoneName: "shortOffset",
    });

    // Example output: "10/28/2025, GMT-4"
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");

    if (!offsetPart || !offsetPart.value) {
      return "GMT+0000";
    }

    // Normalize the offset value
    // Possible formats: "GMT-4", "GMT+5:30", "UTC+01:00", etc.
    const offset = offsetPart.value.replace("UTC", "GMT").replace(/\s/g, "");

    // Standardize to "GMT±HHMM"
    const match = offset.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!match) {
      return "GMT+0000";
    }

    const sign = match[1];
    const hours = match[2].padStart(2, "0");
    const minutes = (match[3] || "00").padStart(2, "0");

    return `GMT${sign}${hours}${minutes}`;
  } catch (error) {
    console.error("Invalid timeZoneId:", timeZoneId, error);
    return "GMT+0000";
  }
}
export function getCurrentDateTimeForAirport(airportCode: string): Moment {
  const config: any = getAirportConfig(airportCode);
  if (config.hardCodedDates && config.defaultStart) {
    return moment.tz(config.defaultStart, config.timeZoneId);
  } else {
    return moment.tz(config.timeZoneId);
  }
}

/**
 * Formats a given time string into a localized time string.
 *
 * @param dateTime - The time string to format. If the value is `undefined` or `null`, it returns "N/A".
 * @param hour12 - Optional. A boolean indicating whether to use 12-hour time (AM/PM) or 24-hour time. Defaults to `false`.
 * @returns The formatted time string in "HH:MM" format based on the `hour12` parameter, or "N/A" if the input time is invalid.
 */
export const formatTime = (
  dateTime: string | undefined | null,
  hour12 = false
) => {
  if (!dateTime) {
    return "N/A";
  }
  const date = new Date(dateTime);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: hour12,
  });
};

/**
 * Converts an ISO date-time string to "HH:mm" using date-fns parseISO.
 * Returns "N/A" if the input is null, undefined, empty, or not a valid date.
 *
 */
export function toHHMM(input: string | null | undefined): string {
  if (!input || typeof input !== "string" || !input.trim()) {
    return "-";
  }

  const date = parseISO(input);
  if (!isValid(date)) {
    return "-";
  }

  return format(date, "HH:mm");
}

/**
 * Returns an object containing the minimum and maximum dates for a calendar date range.
 * The range spans from the start of the previous month to the end of the next month relative to the given date.
 *
 * @param today - The reference date from which the date range is calculated.
 * @returns An object with `minDate` set to the start of the previous month and `maxDate` set to the end of the next month.
 */
export const getCalendarDateRange = (today: Date) => {
  const previousMonth = subMonths(today, 1);
  const nextMonth = addMonths(today, 1);
  return {
    minDate: startOfMonth(previousMonth),
    maxDate: endOfMonth(nextMonth),
  };
};

/**
 * Converts a given date to a specific time zone and returns the corresponding date and time.
 *
 * @param date - The date object to be converted.
 * @param timeZone - The IANA time zone string (e.g., "America/New_York") to which the date should be converted.
 * @returns A new date object representing the date and time in the specified time zone.
 */
export const getDateTimeForTimeZone = (date: Date, timeZone: string) => {
  return toZonedTime(date, timeZone);
};

/**
 * Formats a given Date object into a string suitable for API payloads.
 *
 * @param date - The Date object to be formatted.
 * @returns A string representing the formatted date in the specified payload format.
 */
export const formatDateForApiPayload = (date: Date) => {
  return format(date, payloadDateFormat);
};

/**
 * Converts a given date to UTC by adjusting for the local timezone offset.
 *
 * @param date - The date object to be converted to UTC.
 * @returns A new `Date` object representing the input date in UTC.
 */
export const convertToUTC = (date: Date): Date => {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

/**
 * Converts a given date to a specific time zone or UTC based on the provided parameters.
 *
 * @returns A new `Date` object converted to the specified time zone or UTC.
 */
export const getConvertedTime = (
  date: Date,
  timeZone: string,
  airportTimeZone: string
): Date => {
  if (timeZone === "LT") {
    return toZonedTime(date, airportTimeZone);
  } else {
    return convertToUTC(date);
  }
};

/**
 * Formats a block-time value based on selected timezone and airport timezone.
 * For LT it formats using local date parsing, otherwise it converts airport-zoned time to UTC first.
 */
export const formatBlockTimeByTimeZone = (
  timezone: string,
  airportTimeZone: string,
  myDate: string,
  currentTime: Date,
  type?: string
): string => {
  const isLocalTime = timezone === "LT";
  const blockTimeTypeSuffix = type?.slice(0, 3) || "";

  const blockTime = isLocalTime
    ? new Date(myDate)
    : convertToUTC(fromZonedTime(myDate, airportTimeZone));

  if (!isValid(blockTime)) {
    return "—";
  }

  const dayDiff = differenceInCalendarDays(blockTime, currentTime);

  if (isSameDay(blockTime, currentTime)) {
    return `${format(blockTime, "HH:mm")} ${blockTimeTypeSuffix}`;
  }
  if (dayDiff === 1) {
    return `${format(blockTime, "HH:mm")}+1 ${blockTimeTypeSuffix}`;
  }
  if (dayDiff === -1) {
    return `${format(blockTime, "HH:mm")}-1 ${blockTimeTypeSuffix}`;
  }
  if (isSameYear(blockTime, currentTime)) {
    return `${format(blockTime, "HH:mm MMM dd")} ${blockTimeTypeSuffix}`;
  }
  return `${format(blockTime, "HH:mm MMM dd, yyyy")} ${blockTimeTypeSuffix}`;
};

/**
 * Calculates the rolling time difference in hours between two time points.
 *
 * This function takes into account fractional hours by considering the minutes
 * provided for both the starting and ending time points. It also handles negative
 * hour values correctly, ensuring accurate calculations for time differences
 * that cross over midnight or involve negative time zones.
 *
 * @param fromHours - The starting hour (can be negative).
 * @param fromMinutes - The starting minutes (0-59).
 * @param toHours - The ending hour (can be negative).
 * @param toMinutes - The ending minutes (0-59).
 * @returns The time difference in hours as a floating-point number.
 */
export function calculateRollingTimeDifference(
  fromHours: number,
  fromMinutes: number,
  toHours: number,
  toMinutes: number
): number {
  const totalFromHours =
    fromHours < 0 || Object.is(fromHours, -0)
      ? fromHours - fromMinutes / 60
      : fromHours + fromMinutes / 60;

  const totalToHours =
    toHours < 0 || Object.is(toHours, -0)
      ? toHours - toMinutes / 60
      : toHours + toMinutes / 60;
  return totalToHours - totalFromHours;
}

/**
 * Compares two rolling times to determine if the first time is greater than the second time.
 *
 * @param fromHours - The hour component of the first time.
 * @param fromMinutes - The minute component of the first time.
 * @param toHours - The hour component of the second time.
 * @param toMinutes - The minute component of the second time.
 * @returns `true` if the first time is greater than the second time, otherwise `false`.
 */
export function compareRollingTime(
  fromHours: number,
  fromMinutes: number,
  toHours: number,
  toMinutes: number
): boolean {
  if (fromHours > toHours) {
    return true;
  } else if (fromHours === toHours) {
    if (fromHours < 0 || Object.is(fromHours, -0)) {
      return fromMinutes < toMinutes;
    } else {
      return fromMinutes > toMinutes;
    }
  } else {
    return false;
  }
}

/**
 * Validates the rolling time between two time ranges specified by hours and minutes.
 *
 * @param fromHours - The starting hour of the time range (0-23).
 * @param fromMinutes - The starting minutes of the time range (0-59).
 * @param toHours - The ending hour of the time range (0-23).
 * @param toMinutes - The ending minutes of the time range (0-59).
 * @returns A string containing an error message if the validation fails, or an empty string if the validation passes.
 *
 * The function performs the following validations:
 * - Ensures that the "from" time is less than the "to" time.
 * - Ensures that the rolling time difference does not exceed 24 hours.
 */
export const validateRollingTime = (
  fromHours: number,
  fromMinutes: number,
  toHours: number,
  toMinutes: number
): string => {
  const rollingHoursDifference = calculateRollingTimeDifference(
    fromHours,
    fromMinutes,
    toHours,
    toMinutes
  );

  if (rollingHoursDifference > 24) {
    return "Rolling time should be less than or equal to 24 hours";
  }
  if (rollingHoursDifference <= 0) {
    return "To value must be greater than From value";
  }
  return "";
};

/**
 * Validates the custom date-time values by checking two conditions:
 * 1. Ensures that the "fromDate" is earlier than the "toDate".
 * 2. Ensures that the difference between "fromDate" and "toDate" does not exceed 24 hours.
 *
 * @param fromDate - The starting date and time to validate.
 * @param toDate - The ending date and time to validate.
 * @returns A string containing the error message if validation fails, or an empty string if validation passes.
 */
export const validateCustomDateTimeValues = (
  fromDate: Date,
  toDate: Date
): string => {
  let erroMsg = "";

  // Normalize both dates by setting seconds and milliseconds to zero
  const normalizedFromDate = setMilliseconds(setSeconds(fromDate, 0), 0);
  const normalizedToDate = setMilliseconds(setSeconds(toDate, 0), 0);

  if (
    isEqual(normalizedFromDate, normalizedToDate) ||
    isAfter(normalizedFromDate, normalizedToDate)
  ) {
    erroMsg = "From Date should be less than To Date";
  }
  if (
    differenceInMinutes(normalizedToDate, normalizedFromDate) > MINUTES_IN_DAY
  ) {
    erroMsg =
      "Difference between From Date Time and To Date Time should be less than or equal to 24 hours";
  }
  return erroMsg;
};

export function convertToRollingHours(hour: number, minute: number): number {
  return hour < 0 || Object.is(hour, -0)
    ? hour - minute / 60
    : hour + minute / 60;
}

/**
 * Formats a given date string into a relative time description based on the current date and time zones.
 *
 * @param myDate - The date string to be formatted, expected in ISO format (UTC with 'Z').
 * @param currentDate - The current date used as a reference for calculating the relative time.
 * @param timeZone - The time zone of the current date.
 * @param airportTimeZone - The time zone of the airport or target location.
 * @returns A formatted string representing the relative time:
 *          - "Today HH:mm" if the date is the same as the current date.
 *          - "Yesterday HH:mm" if the date is one day before the current date.
 *          - "dd-MMM-yy HH:mm" for any other date.
 *          Returns an empty string if the input dates are invalid.
 */
export function formatRelativeTimeUTC(
  myDate: string,
  currentDate: Date,
  timeZone: string,
  airportTimeZone: string
): string {
  const parsedUTCDate = parseISO(myDate); // UTC date with 'Z'

  // Validate both dates
  if (!isValid(parsedUTCDate) || !isValid(currentDate)) {
    return "";
  }

  const myDateObj = getConvertedTime(parsedUTCDate, timeZone, airportTimeZone);
  const dayDiff = differenceInCalendarDays(currentDate, myDateObj);
  return getDateTimeDisplayText(dayDiff, myDateObj);
}

/**
 * Formats a relative time string based on the difference between two dates.
 *
 * @param myDate - The date to compare against the current date. If undefined, an empty string is returned.
 * @param currentDate - The current date to calculate the difference from.
 * @returns A string representing the relative time difference between the two dates.
 *          Returns an empty string if `myDate` is undefined or if either date is invalid.
 */
export const formatRelativeTime = (
  myDate: Date | undefined,
  currentDate: Date,
  todayLabel = true
): string => {
  if (!myDate) {
    return "";
  }

  // Validate both dates
  if (!isValid(myDate) || !isValid(currentDate)) {
    return "";
  }

  const dayDiff = differenceInCalendarDays(currentDate, myDate);
  const timeStr = format(myDate, "HH:mm");
  if (isSameDay(currentDate, myDate)) {
    return `${todayLabel ? "Today" : ""} ${timeStr}`;
  }
  if (dayDiff === 1) {
    return `Yesterday ${timeStr} `;
  }
  if (dayDiff === -1) {
    return `Tomorrow ${timeStr}`;
  }
  if (isSameYear(myDate, currentDate)) {
    return format(myDate, "dd-MMM HH:mm");
  }
  return format(myDate, "dd-MMM-yy HH:mm");
};

/**
 * Generates a display text for a given date based on the difference in days from the current date.
 *
 * @param dayDifference - The difference in days between the current date and the provided date.
 *                         A value of 0 represents today, 1 represents yesterday, and other values
 *                         represent dates further in the past.
 * @param date - The date object to format and display.
 * @returns A formatted string representing the date and time. If the date is today, it returns
 *          "Today HH:mm". If the date is yesterday, it returns "Yesterday HH:mm". For other dates,
 *          it returns "dd-MMM-yy HH:mm".
 */
export const getDateTimeDisplayText = (dayDifference: number, date: Date) => {
  const timeStr = format(date, "HH:mm");

  if (dayDifference === 0) {
    return `Today ${timeStr}`;
  } else if (dayDifference === 1) {
    return `Yesterday ${timeStr}`;
  } else if (dayDifference === -1) {
    return `Tomorrow ${timeStr}`;
  } else {
    return format(date, "dd-MMM-yy HH:mm");
  }
};

export const calculateRollingHours = (
  appliedFilterValues: RollingHoursInput
): RollingHoursOutput => {
  const addFromRollingHours =
    appliedFilterValues.fromRollingHours < 0 ||
    Object.is(appliedFilterValues.fromRollingHours, -0)
      ? appliedFilterValues.fromRollingHours -
        appliedFilterValues.fromRollingMinute / 60
      : appliedFilterValues.fromRollingHours +
        appliedFilterValues.fromRollingMinute / 60;

  const addToRollingHours =
    appliedFilterValues.toRollingHours < 0 ||
    Object.is(appliedFilterValues.toRollingHours, -0)
      ? appliedFilterValues.toRollingHours -
        appliedFilterValues.toRollingMinute / 60
      : appliedFilterValues.toRollingHours +
        appliedFilterValues.toRollingMinute / 60;

  return { addFromRollingHours, addToRollingHours };
};

/**
 * Calculates the date range for a rolling or "today" filter based on the provided filter values
 * and the current time.
 *
 * @param appliedFilterValues - The filter values that determine the type of date range to calculate.
 *                              It includes options such as rolling hours or "today" options.
 * @param currentTime - The current date and time used as the reference point for calculations.
 *
 * @returns An object representing the global date filter with `startDate` and `endDate` properties,
 *          or `null` if the filter values do not match the expected options.
 */
export const getRollingFilterDateRange = (
  appliedFilterValues: IAppliedFilterValues,
  currentTime: Date
): IGlobalDateFilter | null => {
  if (appliedFilterValues?.selectedOption === GlobalFilterOptionsEnum.rolling) {
    const { addFromRollingHours, addToRollingHours } = calculateRollingHours({
      fromRollingHours: appliedFilterValues.fromRollingHours,
      fromRollingMinute: appliedFilterValues.fromRollingMinute,
      toRollingHours: appliedFilterValues.toRollingHours,
      toRollingMinute: appliedFilterValues.toRollingMinute,
    });

    return {
      startDate: addHours(currentTime, addFromRollingHours),
      endDate: addHours(currentTime, addToRollingHours),
    };
  }

  if (
    appliedFilterValues?.selectedOption === GlobalFilterOptionsEnum.today &&
    appliedFilterValues?.todayOption === TodayFilterOptionsEnum.now
  ) {
    return {
      startDate: startOfDay(currentTime),
      endDate: currentTime,
    };
  }

  return null;
};
